import { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronDown, Download, Search, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { fmtTs, categoryBadgeClass, downloadCsv } from './_utils';

const CATEGORIES = ['Bookings', 'Assignments', 'Terminal', 'Notes', 'Route Config', 'AI', 'System', 'Auth'];

export default function AuditTrail() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: '', date_to: '',
    categories: new Set(['Bookings', 'Assignments', 'Terminal', 'Notes', 'Route Config', 'AI', 'System', 'Auth']),
    user_filter: '', q: '',
  });
  const [expanded, setExpanded] = useState(new Set());

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.categories.size < CATEGORIES.length)
        params.category = Array.from(filters.categories).join(',');
      if (filters.user_filter) params.user_filter = filters.user_filter;
      if (filters.q) params.q = filters.q;
      const [e, u] = await Promise.all([
        api.get('/routeops/audit', { params }),
        api.get('/routeops/audit/users'),
      ]);
      setEntries(e.data || []);
      setUsers(u.data || []);
    } catch {
      toast.error('Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchEntries, 200);
    return () => clearTimeout(t);
  }, [filters]);

  const toggleCategory = (cat) => {
    const next = new Set(filters.categories);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setFilters({ ...filters, categories: next });
  };

  const selectAllCats = () => setFilters({ ...filters, categories: new Set(CATEGORIES) });

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  const handleExport = () => {
    downloadCsv(entries, [
      { label: 'Timestamp', key: 'timestamp' },
      { label: 'User', key: 'user' },
      { label: 'Role', key: 'user_role' },
      { label: 'Category', key: 'category' },
      { label: 'Action', key: 'action' },
      { label: 'Record', key: 'record' },
      { label: 'Detail', key: 'detail' },
    ], 'routeops-audit.csv');
    toast.success('Audit exported');
  };

  const accentForRow = (e) => {
    if (e.action?.toLowerCase().includes('delete')) return 'border-l-4 border-red-500';
    if (e.action?.toLowerCase().includes('restore')) return 'border-l-4 border-red-500';
    if (e.category === 'AI' || e.action?.toLowerCase().includes('reoptim'))
      return 'border-l-4 border-amber-500';
    return 'border-l-4 border-transparent';
  };

  return (
    <div data-testid="routeops-audit-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b border-slate-200">
        <Shield className="h-5 w-5 text-slate-500" />
        <h1 className="text-2xl font-bold text-slate-900">Audit Trail</h1>
        <span className="text-xs text-slate-500 uppercase tracking-wide">Immutable log — read only</span>
        <Badge variant="outline" className="bg-slate-50 text-slate-600 ml-2">{entries.length} entries</Badge>
      </div>

      {/* Filter bar */}
      <Card className="mb-4">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-slate-500 tracking-wide">From</Label>
              <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="audit-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500 tracking-wide">To</Label>
              <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="audit-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500 tracking-wide">User</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
                value={filters.user_filter}
                onChange={e => setFilters({ ...filters, user_filter: e.target.value })}
                data-testid="audit-user"
              >
                <option value="">All users</option>
                {users.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500 tracking-wide">Search</Label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={filters.q}
                  onChange={e => setFilters({ ...filters, q: e.target.value })}
                  placeholder="Search detail…"
                  className="pl-7"
                  data-testid="audit-search"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase text-slate-500 tracking-wide mr-1">Category:</span>
            <button
              onClick={selectAllCats}
              className="text-[11px] px-2 py-1 bg-slate-900 text-white rounded"
              data-testid="audit-cat-all"
            >All</button>
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => toggleCategory(c)}
                className={`text-[11px] px-2 py-1 border rounded ${
                  filters.categories.has(c) ? categoryBadgeClass(c) + ' font-semibold' : 'bg-white border-slate-200 text-slate-500'
                }`}
                data-testid={`audit-cat-${c.replace(/\s+/g, '-').toLowerCase()}`}
              >{c}</button>
            ))}
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="audit-export">
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Entries */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[200px_180px_120px_180px_1fr_20px] gap-3 px-3 py-2 bg-slate-100 border-b border-slate-200 text-[10px] uppercase font-bold tracking-wide text-slate-600">
          <div>Timestamp</div>
          <div>User</div>
          <div>Category</div>
          <div>Action</div>
          <div>Detail</div>
          <div></div>
        </div>
        {loading && <div className="text-center text-slate-400 py-8">Loading…</div>}
        {!loading && entries.length === 0 && (
          <div className="text-center text-slate-400 py-8">No audit entries</div>
        )}
        <div className="divide-y divide-slate-100">
          {entries.map((e, idx) => {
            const isExp = expanded.has(e.id);
            const detailShort = (e.detail || '').length > 140 && !isExp
              ? (e.detail.slice(0, 140) + '…')
              : e.detail;
            return (
              <div
                key={e.id}
                className={`grid grid-cols-[200px_180px_120px_180px_1fr_20px] gap-3 px-3 py-2 text-xs hover:bg-slate-50 ${accentForRow(e)} ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}
                data-testid={`audit-row-${e.id}`}
              >
                <div className="font-mono text-slate-700 text-[11px]">{fmtTs(e.timestamp)}</div>
                <div>
                  <div className="font-medium text-slate-800">{e.user}</div>
                  {e.user_role && <div className="text-[10px] text-slate-400">{e.user_role}</div>}
                </div>
                <div>
                  <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${categoryBadgeClass(e.category)}`}>
                    {e.category}
                  </span>
                </div>
                <div className="text-slate-700 font-medium">{e.action}</div>
                <div className="text-slate-700 whitespace-pre-wrap">{detailShort}</div>
                <button
                  onClick={() => toggleExpand(e.id)}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExp ? 'rotate-180' : ''}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
